import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ArrowDownUp, Loader2, Settings, ShieldAlert, Zap } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { resolveInventoryPushSettings } from '@/features/organization/components/settingsShared';
import type { OrganizationSettings as SharedOrganizationSettings } from '@threadheads/shared';
import { PushConfirmDialog } from '@/features/stock-sync/components/PushConfirmDialog';
import { useDateFnsLocale } from '@/lib/format-dates';
import { resolveInventoryPushContext } from '../utils/inventoryPushContext';

/**
 * PushToShopifyButton — Inventory action-bar entry point for the outbound
 * Shopify push, so a pure-client org can trigger it without visiting the
 * (owner-oriented) Stock Sync page. Self-contained: gates its own render,
 * fetches its own status, and owns the confirm-dialog flow.
 *
 * Gate mirrors `InventoryProductDetailModal`'s hybrid-push check
 * (`resolveInventoryPushSettings(organization?.settings).enabled`) plus an
 * admin check, since the push mutations below are `adminProcedure`.
 */
const PUSH_TOAST_ID = 'push-to-shopify';

interface PushToShopifyButtonProps {
  /** Specific organization selected in the shared-inventory client filter. */
  viewedOrganizationId?: string | null;
  viewedOrganizationName?: string | null;
  /** True when the unfiltered inventory table contains more than one org. */
  hasMixedOrganizationRows?: boolean;
}

export function PushToShopifyButton({
  viewedOrganizationId,
  viewedOrganizationName,
  hasMixedOrganizationRows = false,
}: PushToShopifyButtonProps = {}) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin } = useRole();
  const utils = trpc.useUtils();
  const dateLocale = useDateFnsLocale();

  const pushSettings = resolveInventoryPushSettings(
    organization?.settings as SharedOrganizationSettings | null,
  );
  const gatePassed = pushSettings.enabled && isAdmin() && !!organization?.shopify_domain;
  const pushContext = resolveInventoryPushContext({
    activeOrganizationId: organization?.id,
    viewedOrganizationId,
    hasMixedOrganizationRows,
  });
  const contextGatePassed = gatePassed && !pushContext.blocked;

  // Both queries below are only useful while the menu is open — the status
  // rows they feed (last push, live halted/dryRun state) aren't visible
  // otherwise, so there's no reason to pay for an 8-round-trip overview
  // fetch (or its 30s poll) just because the Inventory page is mounted.
  const [menuOpen, setMenuOpen] = useState(false);

  const statsQuery = trpc.organization.getInventoryPushStats.useQuery(undefined, {
    enabled: contextGatePassed && menuOpen,
  });

  // Live status — `organization.settings` is a one-shot snapshot from
  // OrganizationContext, so `halted`/`dryRunMode` can go stale while the
  // menu is open. Mirrors StockSyncOverview's `useStockSyncOverview` poll;
  // falls back to the settings snapshot while the first fetch is in flight.
  const overviewQuery = trpc.stockSync.getOverview.useQuery(undefined, {
    enabled: contextGatePassed && menuOpen,
    refetchInterval: menuOpen ? 30_000 : false,
  });

  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewData, setPreviewData] = useState<{
    expectedOrganizationId: string;
    goingToZero: Array<{ sku: string; previousQty: number; newQty: number }>;
    totalChanges: number;
    /** SKUs set to Always 0 that the push writes as 0 (not in goingToZero). */
    alwaysZeroCount?: number;
  } | null>(null);

  // A zero-stock preview authorizes confirmation for exactly the organization
  // that produced it. Switching organizations while the dialog is open must
  // discard that preview instead of allowing an unpreviewed tenant push.
  useEffect(() => {
    if (previewData && previewData.expectedOrganizationId !== organization?.id) {
      setShowConfirm(false);
      setPreviewData(null);
    }
  }, [organization?.id, previewData]);

  const invalidatePushQueries = () => {
    utils.organization.getInventoryPushStats.invalidate();
    utils.stockSync.getOverview.invalidate();
    utils.organization.getOrganization.invalidate();
  };

  const triggerPush = trpc.organization.triggerInventoryPush.useMutation({
    // Radix closes the dropdown on item click, so the in-menu "Pushing…"
    // state is invisible — a loading toast is the only feedback the user
    // gets while the mutation is in flight. Fixed `id` so onSuccess/onError
    // replace it in place rather than stacking a second toast.
    onMutate: () => {
      toast.loading(t('push.pushing', 'Pushing...'), { id: PUSH_TOAST_ID });
    },
    onSuccess: (result) => {
      // A halt (mass-OOS guard) or a disabled-push guard both come back as
      // `success:false` with zero errors and no Shopify error strings — this
      // is a deliberate PAUSE for human review, not a generic failure.
      if (!result.success && result.errors === 0 && !result.shopifyErrors?.length) {
        toast.warning(t('push.toast.paused', 'Push paused for review'), {
          id: PUSH_TOAST_ID,
          action: {
            label: t('push.toast.reviewAction', 'Review'),
            onClick: () => navigate('/stock-sync'),
          },
        });
      } else if (result.success) {
        const total = result.papUpdated + result.podUpdated;
        const prefix = result.dryRun ? t('push.toast.dryRunPrefix', '[Dry run] ') : '';
        if (total === 0) {
          if (result.podSkipped > 0) {
            // t() calls stay OUTSIDE template literals — the i18n parity
            // scanner does not see references nested inside `${...}`.
            const podSkippedMsg = t('push.toast.podSkippedWarning', { count: result.podSkipped });
            toast.warning(prefix + podSkippedMsg, {
              id: PUSH_TOAST_ID,
              description: t('push.toast.podSkippedWarningDescription', 'Check that these products exist in your Shopify store'),
              duration: 6000,
            });
          } else {
            const nothingMsg = t('push.toast.nothingToPush', 'Nothing to push right now.');
            toast.info(prefix + nothingMsg, { id: PUSH_TOAST_ID });
          }
        } else {
          const activatedNote = result.activated
            ? t('push.toast.activatedNote', {
                activatedCount: result.activated,
                defaultValue: ' ({{activatedCount}} activated)',
              })
            : '';
          const skippedNote = result.podSkipped > 0
            ? t('push.toast.skippedNote', {
                skippedCount: result.podSkipped,
                defaultValue: ', {{skippedCount}} on-demand had no variants',
              })
            : '';
          const successMsg = t('push.toast.success', {
            pap: result.papUpdated,
            pod: result.podUpdated,
            defaultValue: 'Push complete: {{pap}} pre-made, {{pod}} on-demand updated',
          });
          toast.success(prefix + successMsg + activatedNote + skippedNote, { id: PUSH_TOAST_ID });
        }
      } else {
        toast.error(t('push.toast.failed', { count: result.errors }), {
          id: PUSH_TOAST_ID,
          description: result.shopifyErrors?.[0],
          duration: 8000,
        });
      }
      invalidatePushQueries();
    },
    onError: (error) => {
      toast.error(`Push failed: ${error.message}`, { id: PUSH_TOAST_ID });
      invalidatePushQueries();
    },
  });

  if (!gatePassed) return null;

  if (pushContext.blocked) {
    const organizationLabel = viewedOrganizationName ?? t('crossOrg.partnerFallback', 'the client organization');
    const label = pushContext.reason === 'cross_organization'
      ? t('push.crossOrg.switch', {
          organization: organizationLabel,
          defaultValue: 'Switch to {{organization}} to push',
        })
      : t('push.crossOrg.mixed', 'Select an organization to push');
    const help = pushContext.reason === 'cross_organization'
      ? t('push.crossOrg.switchHelp', {
          organization: organizationLabel,
          defaultValue: 'This inventory belongs to {{organization}}. Switch to that organization before pushing its Shopify inventory.',
        })
      : t('push.crossOrg.mixedHelp', 'This view contains inventory from several organizations. Filter to your current organization, or switch organizations before pushing.');

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" tabIndex={0} aria-label={help}>
              <Button variant="outline" size="sm" className="h-8 max-w-64 gap-1.5 text-xs" disabled>
                <ShieldAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{label}</span>
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-80">
            <p className="text-xs">{help}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Live overview wins once loaded; settings snapshot covers the gap before
  // the first poll resolves (see comment at `overviewQuery` above).
  const halted = overviewQuery.data ? overviewQuery.data.halted : pushSettings.halted;
  const dryRunMode = overviewQuery.data ? overviewQuery.data.dryRunMode : pushSettings.dryRunMode;
  // Once the live status has loaded, don't let a manual push start when the
  // worker itself reports push disabled — only enforced once we actually
  // know (StockSyncOverview.tsx's `!overview?.pushEnabled` pattern), not
  // before the first fetch resolves.
  const pushDisabledByOverview = overviewQuery.data ? !overviewQuery.data.pushEnabled : false;

  const lastPushDisplay = statsQuery.data?.lastPushAt
    ? formatDistanceToNow(new Date(statsQuery.data.lastPushAt), { addSuffix: true, locale: dateLocale })
    : statsQuery.isLoading
      ? t('common.loading', 'Loading...')
      : t('push.lastPushNever', 'Never');

  const handlePushNow = async () => {
    if (!organization?.id) {
      toast.error(t('push.organizationUnavailable', 'Organization context is unavailable. Refresh and try again.'));
      return;
    }
    const expectedOrganizationId = organization.id;
    setIsPreviewing(true);
    try {
      const preview = await utils.organization.previewInventoryPush.fetch({
        expectedOrganizationId,
      });
      if (preview.goingToZero.length > 0) {
        setPreviewData({ ...preview, expectedOrganizationId });
        setShowConfirm(true);
      } else {
        triggerPush.mutate({ expectedOrganizationId });
      }
    } catch {
      toast.error(t('push.previewError', 'Could not load push preview, please try again'), {
        description: t('push.previewErrorDescription', 'The safety check for zero-stock SKUs was unavailable.'),
        duration: 6000,
      });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleConfirmPush = () => {
    if (!previewData) return;
    triggerPush.mutate({ expectedOrganizationId: previewData.expectedOrganizationId }, {
      onSettled: () => {
        setShowConfirm(false);
        setPreviewData(null);
      },
    });
  };

  const pushInFlight = triggerPush.isPending || isPreviewing;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
            <ArrowDownUp className="h-3 w-3" />
            {t('push.button', 'Push to Shopify')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t('push.menuLabel', 'Push to Shopify')}</DropdownMenuLabel>
          <div className="space-y-1.5 px-2 pb-1.5 pt-0.5">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{t('push.lastPushLabel', 'Last push')}</span>
              <span className="font-medium text-foreground">{lastPushDisplay}</span>
            </div>
            {dryRunMode && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">
                {t('push.dryRunBadge', 'Dry run')}
              </Badge>
            )}
          </div>
          {halted && (
            // Halted → route to the dashboard tab (Approve & Push / Dismiss
            // live there), not Configuration — this row is where it gets
            // resolved, not just where it's explained. A real DropdownMenuItem
            // (not a hand-rolled `role="button"` div) so it's reachable via
            // the menu's roving focus / arrow keys.
            <DropdownMenuItem
              onClick={() => navigate('/stock-sync')}
              className="items-start gap-1.5 whitespace-normal text-[11px] leading-snug text-destructive focus:text-destructive"
            >
              <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{t('push.haltedWarning', 'Push paused: mass out-of-stock detected')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {halted ? (
            // A manual push while halted is a guaranteed no-op that reports
            // as a failure — the worker has no halted check on the manual
            // path. Route to the review surface instead of firing it.
            <DropdownMenuItem onClick={() => navigate('/stock-sync')}>
              <ShieldAlert className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              {t('push.reviewPausedPush', 'Review paused push')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={handlePushNow} disabled={pushInFlight || pushDisabledByOverview}>
              {pushInFlight ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {isPreviewing
                    ? t('push.checking', 'Checking...')
                    : t('push.pushing', 'Pushing...')}
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                  {t('push.pushNow', 'Push all locations now')}
                </>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => navigate(halted ? '/stock-sync' : '/stock-sync?tab=configuration')}
          >
            <Settings className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {t('push.settingsLink', 'Stock Sync settings…')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PushConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirmPush}
        goingToZero={previewData?.goingToZero ?? []}
        totalChanges={previewData?.totalChanges ?? 0}
        alwaysZeroCount={previewData?.alwaysZeroCount ?? 0}
        isPending={triggerPush.isPending}
        mode="bulk"
        isDryRun={dryRunMode}
        hybridQtyEnabled={overviewQuery.data?.hybridQty}
      />
    </>
  );
}
